import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Autocomplete,
  Button,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  IconButton,
  InputAdornment,
  Divider,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  capitalize
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  CloudUpload as CloudUploadIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Description as DescriptionIcon,
  Email as EmailIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { studentService, paymentService, parentService } from '../../services/api';
import dayjs from '../../utils/dayjs';
// Importar el servicio y componente
import { uploadService } from '../../services/uploadService';
import FileUploader from '../../components/FileUploader';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { getStatusColor, getStatusLabel } from '../../utils/statusFormatters';
import DatePickerField from '../../components/DatePickerField';

// Validación del formulario
const schema = yup.object({
  student_id: yup.number().required('Debe seleccionar un estudiante'),
  parent_id: yup.number().nullable(),
  month: yup.string().required('Debe seleccionar un mes'),
  year: yup.number()
    .required('El año es requerido')
    .min(2023, 'Año no válido')
    .max(2030, 'Año no válido'),
  amount: yup.number()
    .required('El monto es requerido')
    .min(1, 'El monto debe ser mayor a 0')
    .typeError('Debe ser un número válido'),
  total_due: yup.number()
    .required('El monto es requerido')
    .min(1, 'El monto debe ser mayor a 0')
    .typeError('Debe ser un número válido'),
  payment_date: yup.string()
    .required('La fecha de pago es requerida')
    .test('not-future', 'La fecha de pago no puede ser futura', (value) => {
      return dayjs(value).isSameOrBefore(dayjs(), 'day');
    }),
  bank_reference: yup.string().nullable(),
  comments: yup.string().nullable(),
  status: yup.string().required('El estado es requerido'),
  //payment_proof_url: yup.string().nullable()
});

const months = [
  { value: 'Enero', label: 'Enero' },
  { value: 'Febrero', label: 'Febrero' },
  { value: 'Marzo', label: 'Marzo' },
  { value: 'Abril', label: 'Abril' },
  { value: 'Mayo', label: 'Mayo' },
  { value: 'Junio', label: 'Junio' },
  { value: 'Julio', label: 'Julio' },
  { value: 'Agosto', label: 'Agosto' },
  { value: 'Septiembre', label: 'Septiembre' },
  { value: 'Octubre', label: 'Octubre' },
  { value: 'Noviembre', label: 'Noviembre' },
  { value: 'Diciembre', label: 'Diciembre' }
];

const statuses = [
  { value: 'paid', label: 'Pagado', color: 'success' },
  { value: 'pending', label: 'Pendiente', color: 'warning' },
  { value: 'baja', label: 'Baja', color: 'error' },
  { value: 'finalizado', label: 'Finalizado', color: 'info' },
  { value: 'alta', label: 'Matrícula', color: 'secondary' }
];

// Configuración de montos
const DEFAULT_AMOUNT = 2500; // Rublos después de junio 2025
const PREVIOUS_AMOUNT = 2000; // Rublos antes de junio 2025


const CreatePayment: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useSelector((state: RootState) => state.auth);
  
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
  
    // Estados para datos relacionados
    const [students, setStudents] = useState<any[]>([]);
    const [selectedStudent, setSelectedStudent] = useState<any>(null);
    const [parents, setParents] = useState<any[]>([]);
    const [existingPayments, setExistingPayments] = useState<any[]>([]);
    const [showExistingDialog, setShowExistingDialog] = useState(false);
  
    // Estados para el historial de pagos del estudiante
    const [studentHistory, setStudentHistory] = useState<any[]>([]);
    const [showHistoryDialog, setShowHistoryDialog] = useState(false);

    // Estados para archivo - ESTOS SON LOS QUE FALTABAN
    const [paymentFile, setPaymentFile] = useState<File | null>(null);
    const [filePreview, setFilePreview] = useState<string | null>(null);
    const [uploadedFile, setUploadedFile] = useState<{
        name: string;
        url: string;
        type: string;
    } | null>(null);
    const [loadingParents, setLoadingParents] = useState(false);
    const [isPartialPayment, setIsPartialPayment] = useState(false);
    const [totalDue, setTotalDue] = useState<number>(DEFAULT_AMOUNT);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    getValues,
    reset
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      student_id: location.state?.studentId || '',
      parent_id: null,
      month: capitalize(dayjs().format('MMMM')),
      year: dayjs().year(),
      amount: getDefaultAmount(dayjs().format('MMMM').toLowerCase(), dayjs().year()),
      payment_date: dayjs().format('YYYY-MM-DD'),
      bank_reference: '',
      comments: '',
      status: 'paid'
    }
  });

    // Función para eliminar archivo
    const handleFileRemove = () => {
        setUploadedFile(null);
        setPaymentFile(null);
        setFilePreview(null);
        //setValue('payment_proof_url', '');
    };

    // Función para manejar upload
    const handleFileUpload = async (file: File) => {
        try {
            const response = await uploadService.uploadPaymentProof(file);
            
            setUploadedFile({
                name: file.name,
                url: response.data.file_url,
                type: file.type
            });
            
            //setValue('payment_proof_url', response.data.file_url);
            
            return response;
        } catch (err: any) {
            throw new Error(err.response?.data?.message || 'Error al subir archivo');
        }
    };

    // Función para determinar el monto por defecto según fecha
    function getDefaultAmount(month: string, year: number) {
        const JUNE_2025 = dayjs('2025-06-01');
        const paymentDate = dayjs(`${year}-${getMonthNumber(month)}-01`);
        
        if (paymentDate.isAfter(JUNE_2025) || paymentDate.isSame(JUNE_2025)) {
            return DEFAULT_AMOUNT;
        }
        return PREVIOUS_AMOUNT;
    }

    // Observar cambios en mes y año para actualizar monto
    const selectedMonth = watch('month');
    const selectedYear = watch('year');
  
    useEffect(() => {
        setValue('amount', getDefaultAmount(selectedMonth, selectedYear));
        setValue('total_due', getDefaultAmount(selectedMonth, selectedYear));
    }, [selectedMonth, selectedYear, setValue]);

    // Cargar estudiantes
    useEffect(() => {
        loadStudents();
    }, []);

    // Cargar padres cuando se selecciona un estudiante
    const selectedStudentId = watch('student_id');
  
    useEffect(() => {
        if (selectedStudentId) {
            loadStudentDetails(selectedStudentId);
            loadStudentParents(selectedStudentId);
            loadExistingPayments(selectedStudentId);
            loadStudentPaymentHistory(selectedStudentId);
        }
    }, [selectedStudentId]);

    useEffect(() => {
        //console.log("Errores actuales del formulario:", errors);
        //console.log("Formulario es válido?", Object.keys(errors).length === 0);
    }, [errors]);

    const loadStudents = async () => {
        try {
            const response = await studentService.getAll({ per_page: 300, status: 'active' });
            setStudents(response.data.data);
        } catch (err) {
            console.error('Error obteniendo estudiantes:', err);
        }
    };

    const loadStudentDetails = async (studentId: number) => {
        try {
            const response = await studentService.getById(studentId);
            setSelectedStudent(response.data);
        } catch (err) {
            console.error('Error obteniendo detalles del estudiante:', err);
        }
    };

    const loadStudentParents = async (studentId: number) => {
      try {
        setLoadingParents(true);
        let parentsList: any[] = [];
        let defaultParentId: number | null = null;

        // PRIORIDAD 1: Obtener el padre directamente del estudiante (para preselección)
        try {
          const studentResponse = await studentService.getById(studentId);
          const student = studentResponse.data;
          
          if (student.parent_id) {
            defaultParentId = student.parent_id;
            console.log('Padre preseleccionado desde student.parent_id:', defaultParentId);
          }
        } catch (err) {
          console.error('Error loading student:', err);
        }

        // Cargar TODOS los padres activos (para que el usuario pueda elegir cualquiera)
        try {
          const allParentsResponse = await parentService.getAll({ 
            per_page: 500, // Suficiente para todos los padres
            active: true 
          });
          
          if (allParentsResponse.data?.data) {
            parentsList = allParentsResponse.data.data;
            console.log('Todos los padres activos cargados:', parentsList.length);
          }
        } catch (err) {
          console.error('Error loading all parents:', err);
        }

        // Si aún no hay padres, intentar cargar inactivos también
        if (parentsList.length === 0) {
          try {
            const allParentsResponse = await parentService.getAll({ 
              per_page: 500 
            });
            
            if (allParentsResponse.data?.data) {
              parentsList = allParentsResponse.data.data;
              console.log('Todos los padres (incluyendo inactivos) cargados');
            }
          } catch (err) {
            console.error('Error loading all parents (including inactive):', err);
          }
        }

        setParents(parentsList);
        
        // Preseleccionar el padre del estudiante si existe y está en la lista
        if (defaultParentId && parentsList.some(p => p.id === defaultParentId)) {
          setValue('parent_id', defaultParentId);
          console.log('Padre preseleccionado:', defaultParentId);
        } else if (parentsList.length === 1 && !defaultParentId) {
          // Si solo hay un padre y no hay preselección, seleccionarlo automáticamente
          setValue('parent_id', parentsList[0].id);
          console.log('Único padre seleccionado automáticamente');
        }
        
      } catch (err) {
        console.error('Error in loadStudentParents:', err);
      } finally {
        setLoadingParents(false);
      }
    };

  const loadExistingPayments = async (studentId: number) => {
    try {
      const response = await paymentService.getAllMonthly({ 
        student_id: studentId,
        month: selectedMonth,
        year: selectedYear
      });
      
      if (response.data.data.length > 0) {
        setExistingPayments(response.data.data);
        setShowExistingDialog(true);
      }
    } catch (err) {
      //console.error('Error checking existing payments:', err);
    }
  };

  const loadStudentPaymentHistory = async (studentId: number) => {
    try {
      const response = await studentService.getPayments(studentId);
      setStudentHistory(response.data.data);
    } catch (err) {
      //console.error('Error loading payment history:', err);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validar tipo de archivo
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        setError('Solo se permiten imágenes (JPEG, PNG, GIF) y archivos PDF');
        return;
      }
      
      // Validar tamaño (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('El archivo no puede ser mayor a 5MB');
        return;
      }

      setPaymentFile(file);
      
      // Crear preview para imágenes
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFilePreview(e.target?.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }
      
      setError(null);
    }
  };

  const handleRemoveFile = () => {
    setPaymentFile(null);
    setFilePreview(null);
  };

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      setError(null);
      //console.log("Submit llamado");

      // Preparar datos del pago
       // Si hay un archivo pero no está en uploadedFile, subirlo primero
        if (paymentFile && !uploadedFile) {
            const uploadResponse = await handleFileUpload(paymentFile);
            data.payment_proof_url = uploadResponse.data.file_url;
        }

      // Crear el pago
      await paymentService.createMonthly(data);

      setSuccess(true);
      
      // Mostrar mensaje de éxito y redirigir
      setTimeout(() => {
            navigate('/monthly-payments');
      }, 2000);

    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al registrar el pago');
    } finally {
      setLoading(false);
    }
  };

  // Función para obtener número de mes
  function getMonthNumber(monthName: string): number {
    const monthMap: { [key: string]: number } = {
      'Enero': 1, 'Febrero': 2, 'Marzo': 3, 'Abril': 4,
      'Mayo': 5, 'Junio': 6, 'Julio': 7, 'Agosto': 8,
      'Septiembre': 9, 'Octubre': 10, 'Noviembre': 11, 'Diciembre': 12
    };
    return monthMap[monthName] || 1;
  }

return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          Registrar Nuevo Pago
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          ¡Pago registrado exitosamente! Redirigiendo...
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          {/* Selección de Estudiante */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  1. Seleccionar Estudiante
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12}}>
                    <Controller
                      name="student_id"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          options={students}
                          getOptionLabel={(option) => `${option.first_name} ${option.last_name} - ${option.grade}`}
                          isOptionEqualToValue={(option, value) => option.id === value?.id}
                          value={students.find(s => s.id === field.value) || null}
                          onChange={(_, newValue) => {
                            field.onChange(newValue?.id || null);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Estudiante *"
                              error={!!errors.student_id}
                              helperText={errors.student_id?.message}
                            />
                          )}
                          loading={loading}
                          loadingText="Cargando estudiantes..."
                          noOptionsText="No hay estudiantes disponibles"
                          filterOptions={(options, state) => {
                            if (!state.inputValue) return options;
                            
                            const inputValue = state.inputValue.toLowerCase().trim();
                            
                            return options.filter(option => {
                              // Validar y convertir a string cada campo
                              const firstName = option.first_name?.toString().toLowerCase() || '';
                              const lastName = option.last_name?.toString().toLowerCase() || '';
                              const fullName = `${firstName} ${lastName}`.trim();
                              
                              const grade = option.grade?.toString().toLowerCase() || '';
                              const sponsor = option.sponsor?.toString().toLowerCase() || '';
                              
                              // Buscar en todos los campos
                              return fullName.includes(inputValue) || 
                                    grade.includes(inputValue) || 
                                    sponsor.includes(inputValue);
                            });
                          }}
                          /*renderOption={(props, option) => (
                            <li {...props}>
                              <Box display="flex" flexDirection="column">
                                <Typography variant="body1">
                                  {option.first_name} {option.last_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Grado: {option.grade} {option.sponsor ? `| Patrocinador: ${option.sponsor}` : ''}
                                </Typography>
                              </Box>
                            </li>
                          )}*/
                         renderOption={(props, option) => {
                          // Extraer la key del objeto props
                          const { key, ...otherProps } = props;
                          return (
                            <li key={key} {...otherProps}>
                              <Box display="flex" flexDirection="column">
                                <Typography variant="body1">
                                  {option.first_name} {option.last_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Grado: {option.grade} {option.sponsor ? `| Patrocinador: ${option.sponsor}` : ''}
                                </Typography>
                              </Box>
                            </li>
                          );
                        }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Información del Estudiante Seleccionado */}
          {selectedStudent && (
            <Grid size={{ xs: 12 }}>
              <Card variant="outlined" sx={{ bgcolor: 'primary.50' }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Estudiante
                      </Typography>
                      <Typography variant="h6">
                        {selectedStudent.first_name} {selectedStudent.last_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Grado: {selectedStudent.grade}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Estado Actual
                      </Typography>
                      <Chip
                        label={selectedStudent.status === 'active' ? 'Activo' : selectedStudent.status}
                        color={selectedStudent.status === 'active' ? 'success' : 'default'}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Último pago: {selectedStudent.months_without_payment || 0} meses
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Button
                        startIcon={<HistoryIcon />}
                        size="small"
                        onClick={() => setShowHistoryDialog(true)}
                      >
                        Ver Historial de Pagos
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Datos del Pago */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  2. Datos del Pago
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="month"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.month}>
                          <InputLabel>Mes *</InputLabel>
                          <Select {...field} label="Mes *">
                            {months.map((month) => (
                              <MenuItem key={month.value} value={month.value}>
                                {month.label}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.month && (
                            <FormHelperText>{errors.month.message}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="year"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          label="Año *"
                          error={!!errors.year}
                          helperText={errors.year?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Controller
                      name="amount"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          onWheel={(e) => {
                            e.preventDefault();
                            e.currentTarget.blur(); // Quita el foco
                          }}
                          label="Monto (RUB) *"
                          error={!!errors.amount}
                          helperText={errors.amount?.message}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">₽</InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <Chip
                                  size="small"
                                  label={getDefaultAmount(selectedMonth, selectedYear) === DEFAULT_AMOUNT ? 
                                    'Desde Jun 2025' : 'Hasta May 2025'}
                                  color="info"
                                />
                              </InputAdornment>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>
                  {/*<Grid size={{ xs: 12 }}>
                    <Controller
                      name="payment_date"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="date"
                          label="Fecha de Pago *"
                          InputLabelProps={{ shrink: true }}
                          error={!!errors.payment_date}
                          helperText={errors.payment_date?.message}
                        />
                      )}
                    />
                  </Grid>*/}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="payment_date"
                      control={control}
                      render={({ field }) => (
                        <DatePickerField
                          {...field}
                          label="Fecha de Ingreso"
                          error={!!errors.payment_date}
                          helperText={errors.payment_date?.message}
                          disabled={loading}
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isPartialPayment}
                          onChange={(e) => setIsPartialPayment(e.target.checked)}
                        />
                      }
                      label="Pago parcial (permitir pagos en cuotas)"
                    />
                  </Grid>

                  {isPartialPayment && (
                    <Grid size={{ xs: 12 }}>
                      <Controller
                        name="total_due"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            type="number"
                            label="Monto total a pagar"
                            helperText="Monto completo de la mensualidad (aunque se pague en partes)"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₽</InputAdornment>,
                              readOnly: true
                            }}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Selección de Padre */}
          <Grid size={{ xs: 12}}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    3. Seleccionar Padre/Tutor
                  </Typography>
                  {parents.length > 0 && (
                    <Chip
                      icon={<PersonIcon />}
                      label={`${parents.length} padres registrados`}
                      size="small"
                    />
                  )}
                </Box>
                
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12}}>
                    {/*<Controller
                      name="parent_id"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.parent_id}>
                          <InputLabel>Padre/Tutor</InputLabel>
                          <Select {...field} label="Padre/Tutor">
                            <MenuItem value="">Seleccionar (opcional)</MenuItem>
                            {parents.map((parent) => (
                              <MenuItem key={parent.id} value={parent.id}>
                                <Box>
                                  <Typography>
                                    {parent.spanish_name || parent.russian_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {parent.email || parent.phone}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            Selecciona el padre que realizó el pago
                          </FormHelperText>
                        </FormControl>
                      )}
                    />*/}
                    <Controller
  name="parent_id"
  control={control}
  render={({ field }) => (
    <Autocomplete
      options={parents}
      getOptionLabel={(option) => {
        if (!option) return '';
        const displayName = option.spanish_name || option.russian_name;
        const contact = option.email || option.phone;
        return contact ? `${displayName} (${contact})` : displayName;
      }}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      value={parents.find(p => p.id === field.value) || null}
      onChange={(_, newValue) => {
        field.onChange(newValue?.id || null);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Padre/Tutor"
          error={!!errors.parent_id}
          helperText={errors.parent_id?.message || "Selecciona el padre que realizó el pago"}
        />
      )}
      loading={loadingParents}
      loadingText="Cargando padres..."
      noOptionsText="No hay padres disponibles"
      filterOptions={(options, state) => {
        if (!state.inputValue) return options;
        
        const inputValue = state.inputValue.toLowerCase().trim();
        
        return options.filter(option => {
          const spanishName = option.spanish_name?.toString().toLowerCase() || '';
          const russianName = option.russian_name?.toString().toLowerCase() || '';
          const email = option.email?.toString().toLowerCase() || '';
          const phone = option.phone?.toString().toLowerCase() || '';
          const notes = option.notes?.toString().toLowerCase() || '';
          
          return spanishName.includes(inputValue) ||
                 russianName.includes(inputValue) ||
                 email.includes(inputValue) ||
                 phone.includes(inputValue) ||
                 notes.includes(inputValue);
        });
      }}
      renderOption={(props, option) => (
        <li {...props}>
          <Box display="flex" flexDirection="column" width="100%">
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body1">
                {option.spanish_name || option.russian_name}
              </Typography>
              {option.is_active === false && (
                <Chip label="Inactivo" size="small" color="default" />
              )}
            </Box>
            <Box display="flex" gap={2} flexWrap="wrap">
              {option.email && (
                <Typography variant="caption" color="text.secondary" display="flex" alignItems="center">
                  <EmailIcon sx={{ fontSize: 12, mr: 0.5 }} />
                  {option.email}
                </Typography>
              )}
              {option.phone && (
                <Typography variant="caption" color="text.secondary" display="flex" alignItems="center">
                  <PhoneIcon sx={{ fontSize: 12, mr: 0.5 }} />
                  {option.country_code} {option.phone}
                </Typography>
              )}
              {option.russian_name && option.spanish_name && (
                <Typography variant="caption" color="text.secondary">
                  Ruso: {option.russian_name}
                </Typography>
              )}
            </Box>
          </Box>
        </li>
      )}
    />
  )}
/>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Información Adicional */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  4. Información Adicional
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Controller
                      name="bank_reference"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Referencia Bancaria"
                          helperText="Número de referencia o comprobante del banco"
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Controller
                      name="comments"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          multiline
                          rows={3}
                          label="Comentarios"
                          placeholder="Información adicional sobre el pago..."
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Comprobante de Pago
                    </Typography>
                    
                    <input
                      type="file"
                      id="payment-file-upload"
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                      accept=".jpg,.jpeg,.png,.gif,.pdf"
                    />
                    
                    {paymentFile ? (
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Box display="flex" alignItems="center">
                          {filePreview ? (
                            <Box
                              component="img"
                              src={filePreview}
                              alt="Preview"
                              sx={{
                                width: 80,
                                height: 80,
                                objectFit: 'cover',
                                borderRadius: 1,
                                mr: 2
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 80,
                                height: 80,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'grey.200',
                                borderRadius: 1,
                                mr: 2
                              }}
                            >
                              {paymentFile.type === 'application/pdf' ? (
                                <PictureAsPdfIcon sx={{ fontSize: 40, color: 'error.main' }} />
                              ) : (
                                <DescriptionIcon sx={{ fontSize: 40, color: 'info.main' }} />
                              )}
                            </Box>
                          )}
                          
                          <Box flex={1}>
                            <Typography variant="subtitle2">
                              {paymentFile.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {(paymentFile.size / 1024).toFixed(1)} KB
                            </Typography>
                          </Box>
                          
                          <Box>
                            {/*<Button
                              variant="contained"
                              size="small"
                              onClick={() => handleFileUpload(paymentFile)}
                              sx={{ mr: 1 }}
                            >
                              Subir
                            </Button>
                                */}
                            <IconButton
                              size="small"
                              color="error"
                              onClick={handleFileRemove}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      </Paper>
                    ) : (
                      <Paper
                        sx={{
                          border: '2px dashed',
                          borderColor: 'grey.300',
                          borderRadius: 2,
                          p: 3,
                          textAlign: 'center',
                          bgcolor: 'grey.50',
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'primary.50'
                          }
                        }}
                        onClick={() => document.getElementById('payment-file-upload')?.click()}
                      >
                        <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                        <Typography>
                          Haz clic para seleccionar un archivo
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Formatos: JPG, PNG, GIF, PDF (máx. 5MB)
                        </Typography>
                      </Paper>
                    )}
                    
                    {uploadedFile && (
                      <Alert severity="success" sx={{ mt: 2 }}>
                        Archivo subido exitosamente: {uploadedFile.name}
                      </Alert>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Estado del Pago */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  5. Estado del Pago
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.status}>
                          <InputLabel>Estado *</InputLabel>
                          <Select {...field} label="Estado *">
                            {statuses.map((status) => (
                              <MenuItem key={status.value} value={status.value}>
                                <Box display="flex" alignItems="center">
                                  <Chip
                                    label={status.label}
                                    color={status.color as any}
                                    size="small"
                                    sx={{ mr: 1 }}
                                  />
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Botones */}
          <Grid size={{ xs: 12 }}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading || success}
                //disabled={loading || success || (paymentFile && !uploadedFile)}
              >
                {loading ? <CircularProgress size={24} /> : 'Registrar Pago'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>

      {/* Dialog de pagos existentes */}
      <Dialog open={showExistingDialog} onClose={() => setShowExistingDialog(false)}>
        <DialogTitle>Pago Existente</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Ya existe un pago para este estudiante en el mes y año seleccionado.
          </Typography>
          {existingPayments.map((payment) => (
            <Alert key={payment.id} severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Estado:</strong> {getStatusLabel(payment.status)}
              </Typography>
              <Typography variant="body2">
                <strong>Fecha:</strong> {dayjs(payment.payment_date).format('DD/MM/YYYY')}
              </Typography>
              <Typography variant="body2">
                <strong>Monto:</strong> ₽{payment.amount}
              </Typography>
            </Alert>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExistingDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={() => setShowExistingDialog(false)}
            color="warning"
          >
            Continuar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de historial */}
      <Dialog
        open={showHistoryDialog}
        onClose={() => setShowHistoryDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Historial de Pagos - {selectedStudent?.first_name} {selectedStudent?.last_name}
        </DialogTitle>
        <DialogContent>
          <List>
            {studentHistory.length > 0 ? (
              studentHistory.map((payment) => (
                <ListItem key={payment.id} divider>
                  <ListItemAvatar>
                    <Avatar>
                      <ReceiptIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={`${payment.month} ${payment.year}`}
                    secondary={`Monto: ₽${payment.amount} - ${dayjs(payment.payment_date).format('DD/MM/YYYY')}`}
                  />
                  <Chip
                    label={getStatusLabel(payment.status)}
                    color={getStatusColor(payment.status) as any}
                    size="small"
                  />
                </ListItem>
              ))
            ) : (
              <Alert severity="info">No hay pagos registrados</Alert>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowHistoryDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};


export default CreatePayment;